import { useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { getSettings, updateSettings, resolveImages } from '../../api';
import { useSettings } from '../../context/SettingsContext';
import ImageUploader from '../../components/admin/ImageUploader';
import { useToast } from '../../components/Toast';
import Loader from '../../components/Loader';

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [logo, setLogo] = useState([]);
  const [aboutImage, setAboutImage] = useState([]);
  const [whyImage, setWhyImage] = useState([]);
  const [saving, setSaving] = useState(false);
  const { toast, ToastHost } = useToast();

  useEffect(() => {
    getSettings().then((d) => {
      setS(d);
      setLogo(d.logo ? [d.logo] : []);
      setAboutImage(d.aboutImage ? [d.aboutImage] : []);
      setWhyImage(d.home?.whyImage ? [d.home.whyImage] : []);
    });
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setHome = (k, v) => setS((p) => ({ ...p, home: { ...(p.home || {}), [k]: v } }));

  const setStat = (i, key, val) => set('aboutStats', s.aboutStats.map((st, idx) => idx === i ? { ...st, [key]: val } : st));
  const addStat = () => set('aboutStats', [...(s.aboutStats || []), { label: '', value: '' }]);
  const removeStat = (i) => set('aboutStats', s.aboutStats.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const [logoUrl] = await resolveImages(logo);
      const [aboutUrl] = await resolveImages(aboutImage);
      const [whyUrl] = await resolveImages(whyImage);
      const payload = {
        ...s,
        logo: logoUrl || s.logo,
        aboutImage: aboutUrl || '',
        home: { ...(s.home || {}), whyImage: whyUrl || '' },
      };
      await updateSettings(payload);
      await refresh();
      toast('Settings saved');
    } catch (err) { toast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  if (!s) return <Loader />;

  return (
    <form onSubmit={save}>
      <div className="acontent__head"><h1>Settings</h1><button className="btn btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button></div>

      <div className="acard">
        <h3>Brand</h3>
        <div className="form-grid">
          <div className="field"><label>Site name</label><input className="input" value={s.siteName || ''} onChange={(e) => set('siteName', e.target.value)} /></div>
          <div className="field"><label>Tagline</label><input className="input" value={s.tagline || ''} onChange={(e) => set('tagline', e.target.value)} /></div>
          <div className="field full"><label>Logo</label><ImageUploader value={logo} onChange={setLogo} multiple={false} label="Upload logo" /></div>
        </div>
      </div>

      <div className="acard">
        <h3>WhatsApp (floating button & enquiry)</h3>
        <div className="form-grid">
          <div className="field"><label>WhatsApp number (with country code, digits only)</label><input className="input" value={s.whatsappNumber || ''} onChange={(e) => set('whatsappNumber', e.target.value)} placeholder="919999999999" /></div>
          <div className="field flex center gap"><button type="button" className={`toggle ${s.whatsappEnabled ? 'on' : ''}`} onClick={() => set('whatsappEnabled', !s.whatsappEnabled)} /><label style={{ margin: 0 }}>Show floating WhatsApp button</label></div>
          <div className="field full">
            <label>Default message — floating button & Contact page</label>
            <input className="input" value={s.whatsappMessage || ''} onChange={(e) => set('whatsappMessage', e.target.value)} placeholder="Hi! I have a query about your products." />
          </div>
          <div className="field full">
            <label>Product page message</label>
            <input className="input" value={s.whatsappProductMessage || ''} onChange={(e) => set('whatsappProductMessage', e.target.value)} placeholder='Hi! I am interested in "{product}" {color}. {url}' />
          </div>
          <div className="field full">
            <label>Service page message</label>
            <input className="input" value={s.whatsappServiceMessage || ''} onChange={(e) => set('whatsappServiceMessage', e.target.value)} placeholder='Hi! I would like to know more about your "{service}" service. {url}' />
          </div>
          <div className="field full">
            <label>Enquiry-form heading (when sending a filled form on WhatsApp)</label>
            <input className="input" value={s.whatsappEnquiryHeading || ''} onChange={(e) => set('whatsappEnquiryHeading', e.target.value)} placeholder="Enquiry about {item}" />
          </div>
          <p className="muted full" style={{ fontSize: 13 }}>
            Placeholders you can use: <code>{'{product}'}</code> <code>{'{color}'}</code> <code>{'{service}'}</code> <code>{'{item}'}</code> <code>{'{url}'}</code> <code>{'{site}'}</code> — they're filled in automatically, and dropped if empty.
          </p>
        </div>
      </div>

      <div className="acard">
        <h3>Product display (applies to all products)</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>Master switches for the whole catalogue. A product shows its price/colours only when the global switch here <strong>and</strong> the product's own toggle are both on.</p>
        <div className="form-grid">
          <div className="field flex center gap"><button type="button" className={`toggle ${s.showProductPrice !== false ? 'on' : ''}`} onClick={() => set('showProductPrice', s.showProductPrice === false)} /><label style={{ margin: 0 }}>Show price on website</label></div>
          <div className="field flex center gap"><button type="button" className={`toggle ${s.showProductColors !== false ? 'on' : ''}`} onClick={() => set('showProductColors', s.showProductColors === false)} /><label style={{ margin: 0 }}>Show colours on website</label></div>
        </div>
      </div>

      <div className="acard">
        <h3>Hero (home top)</h3>
        <div className="form-grid">
          <div className="field full"><label>Hero title</label><input className="input" value={s.heroTitle || ''} onChange={(e) => set('heroTitle', e.target.value)} /></div>
          <div className="field full"><label>Hero subtitle</label><input className="input" value={s.heroSubtitle || ''} onChange={(e) => set('heroSubtitle', e.target.value)} /></div>
        </div>
      </div>

      <div className="acard">
        <h3>Home section headings</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>Edit the eyebrow/title for each home section. The items inside them are managed in <strong>Home Content</strong>.</p>
        <div className="form-grid">
          <div className="field"><label>Shop by category — eyebrow</label><input className="input" value={s.home?.categoriesEyebrow || ''} onChange={(e) => setHome('categoriesEyebrow', e.target.value)} /></div>
          <div className="field"><label>Shop by category — title</label><input className="input" value={s.home?.categoriesTitle || ''} onChange={(e) => setHome('categoriesTitle', e.target.value)} /></div>
          <div className="field"><label>Featured products — eyebrow</label><input className="input" value={s.home?.featuredEyebrow || ''} onChange={(e) => setHome('featuredEyebrow', e.target.value)} /></div>
          <div className="field"><label>Featured products — title</label><input className="input" value={s.home?.featuredTitle || ''} onChange={(e) => setHome('featuredTitle', e.target.value)} /></div>
          <div className="field"><label>How it works — eyebrow</label><input className="input" value={s.home?.processEyebrow || ''} onChange={(e) => setHome('processEyebrow', e.target.value)} /></div>
          <div className="field"><label>How it works — title</label><input className="input" value={s.home?.processTitle || ''} onChange={(e) => setHome('processTitle', e.target.value)} /></div>
          <div className="field full"><label>How it works — subtitle</label><input className="input" value={s.home?.processSubtitle || ''} onChange={(e) => setHome('processSubtitle', e.target.value)} /></div>
          <div className="field"><label>Why us — eyebrow</label><input className="input" value={s.home?.whyEyebrow || ''} onChange={(e) => setHome('whyEyebrow', e.target.value)} /></div>
          <div className="field"><label>Why us — title</label><input className="input" value={s.home?.whyTitle || ''} onChange={(e) => setHome('whyTitle', e.target.value)} /></div>
          <div className="field full"><label>Why us — image</label><ImageUploader value={whyImage} onChange={setWhyImage} multiple={false} label="Upload Why section image" /></div>
          <div className="field"><label>Who we serve — eyebrow</label><input className="input" value={s.home?.industriesEyebrow || ''} onChange={(e) => setHome('industriesEyebrow', e.target.value)} /></div>
          <div className="field"><label>Who we serve — title</label><input className="input" value={s.home?.industriesTitle || ''} onChange={(e) => setHome('industriesTitle', e.target.value)} /></div>
          <div className="field"><label>Services — eyebrow</label><input className="input" value={s.home?.servicesEyebrow || ''} onChange={(e) => setHome('servicesEyebrow', e.target.value)} /></div>
          <div className="field"><label>Services — title</label><input className="input" value={s.home?.servicesTitle || ''} onChange={(e) => setHome('servicesTitle', e.target.value)} /></div>
          <div className="field"><label>Testimonials — eyebrow</label><input className="input" value={s.home?.testimonialsEyebrow || ''} onChange={(e) => setHome('testimonialsEyebrow', e.target.value)} /></div>
          <div className="field"><label>Testimonials — title</label><input className="input" value={s.home?.testimonialsTitle || ''} onChange={(e) => setHome('testimonialsTitle', e.target.value)} /></div>
        </div>
      </div>

      <div className="acard">
        <h3>Call-to-action band (bottom of home page)</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>The highlighted banner at the end of the home page.</p>
        <div className="form-grid">
          <div className="field flex center gap">
            <button type="button" className={`toggle ${s.home?.ctaEnabled !== false ? 'on' : ''}`} onClick={() => setHome('ctaEnabled', s.home?.ctaEnabled === false)} />
            <label style={{ margin: 0 }}>Show this section</label>
          </div>
          <div className="field" />
          <div className="field full"><label>Title</label><input className="input" value={s.home?.ctaTitle || ''} onChange={(e) => setHome('ctaTitle', e.target.value)} placeholder="Ready to automate your car wash?" /></div>
          <div className="field full"><label>Subtitle</label><input className="input" value={s.home?.ctaSubtitle || ''} onChange={(e) => setHome('ctaSubtitle', e.target.value)} /></div>
          <div className="field"><label>Button text</label><input className="input" value={s.home?.ctaButtonText || ''} onChange={(e) => setHome('ctaButtonText', e.target.value)} placeholder="Get a free consultation" /></div>
          <div className="field"><label>Button link</label><input className="input" value={s.home?.ctaButtonLink || ''} onChange={(e) => setHome('ctaButtonLink', e.target.value)} placeholder="/contact" /></div>
        </div>
      </div>

      <div className="acard">
        <h3>About page</h3>
        <div className="form-grid">
          <div className="field full"><label>About title</label><input className="input" value={s.aboutTitle || ''} onChange={(e) => set('aboutTitle', e.target.value)} /></div>
          <div className="field full"><label>About content</label><textarea className="textarea" style={{ minHeight: 140 }} value={s.aboutContent || ''} onChange={(e) => set('aboutContent', e.target.value)} /></div>
          <div className="field full"><label>About image</label><ImageUploader value={aboutImage} onChange={setAboutImage} multiple={false} label="Upload about image" /></div>
        </div>
        <div className="between" style={{ margin: '10px 0' }}>
          <strong>Stats</strong>
          <button type="button" className="btn btn-ghost btn-sm" onClick={addStat}><FiPlus /> Add stat</button>
        </div>
        {(s.aboutStats || []).map((st, i) => (
          <div className="flex gap" key={i} style={{ marginBottom: 10 }}>
            <input className="input" placeholder="Value (e.g. 1.2k+)" value={st.value} onChange={(e) => setStat(i, 'value', e.target.value)} />
            <input className="input" placeholder="Label (e.g. Clients)" value={st.label} onChange={(e) => setStat(i, 'label', e.target.value)} />
            <button type="button" className="icon-btn danger" onClick={() => removeStat(i)}><FiTrash2 /></button>
          </div>
        ))}
      </div>

      <div className="acard">
        <h3>Contact & socials</h3>
        <div className="form-grid">
          <div className="field"><label>Email</label><input className="input" value={s.email || ''} onChange={(e) => set('email', e.target.value)} /></div>
          <div className="field"><label>Phone</label><input className="input" value={s.phone || ''} onChange={(e) => set('phone', e.target.value)} /></div>
          <div className="field full"><label>Address</label><input className="input" value={s.address || ''} onChange={(e) => set('address', e.target.value)} /></div>
          <div className="field"><label>Instagram URL</label><input className="input" value={s.instagram || ''} onChange={(e) => set('instagram', e.target.value)} /></div>
          <div className="field"><label>Facebook URL</label><input className="input" value={s.facebook || ''} onChange={(e) => set('facebook', e.target.value)} /></div>
          <div className="field"><label>Twitter URL</label><input className="input" value={s.twitter || ''} onChange={(e) => set('twitter', e.target.value)} /></div>
          <div className="field"><label>LinkedIn URL</label><input className="input" value={s.linkedin || ''} onChange={(e) => set('linkedin', e.target.value)} /></div>
        </div>
      </div>

      <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
      <ToastHost />
    </form>
  );
}
